import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ShoppingCart, DollarSign, TrendingUp, Receipt, ArrowRight } from 'lucide-react';
import { LoadingSpinner } from '../components/layout/SharedComponents';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, ComposedChart, Area,
} from 'recharts';
import DrillDownSheet from './DataFilesAnalytics/DrillDownSheet';

const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
const STATUS_COLORS = {
  DRAFT: '#94a3b8',
  CONFIRMED: '#6366f1',
  DISPATCHED: '#06b6d4',
  INVOICED: '#f59e0b',
  CLOSED: '#10b981',
  REJECTED: '#ef4444',
  CANCELLED: '#71717a',
};

function StatCard({ icon: Icon, label, value, sub, accent = 'bg-zinc-100 text-zinc-600', onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-xl border bg-card p-5 transition-shadow ${interactive ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : 'hover:shadow-sm'}`}
      data-testid={`so-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</div>
          <div className="text-2xl font-bold mt-1 tabular-nums text-zinc-900">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <div className={`p-2 rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, span = 'col-span-2', onClick }) {
  const interactive = typeof onClick === 'function';
  return (
    <div
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-xl border bg-card p-5 ${span} transition-shadow ${interactive ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : ''}`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        {interactive && <ArrowRight className="h-3.5 w-3.5 text-zinc-400" />}
      </div>
      <div className="h-64">{children}</div>
    </div>
  );
}

const fmtMoney = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtMoneyShort = (v) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v || 0}`;

export default function SalesOrdersDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const navigate = useNavigate();
  const goOrders = () => navigate('/sales-orders');

  const openOrdersDrill = (queryKey, value, title) => {
    setDrill({
      title: `${title}: ${value}`,
      fetchUrl: `/api/sales-orders?page_size=500`,
      filter: { key: queryKey, value },
      columns: [
        { key: 'so_number', label: 'SO' },
        { key: 'customer_name', label: 'Customer' },
        { key: 'status', label: 'Status' },
        { key: 'order_gross_total', label: 'Total', align: 'right', format: (v) => v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—' },
      ],
      dataPath: 'data',
    });
  };
  const openCustomerOrdersDrill = (customerName) => openOrdersDrill('customer_name', customerName, 'Orders for');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/api/sales-orders-dashboard');
        setData(r.data);
      } catch (e) {
        // ignore
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="py-12"><LoadingSpinner /></div>;
  if (!data) return <div className="py-12 text-center text-sm text-muted-foreground">No data yet.</div>;

  const s = data.summary || {};

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={ShoppingCart} label="Total Orders" value={s.total || 0} accent="bg-indigo-50 text-indigo-600" onClick={goOrders} />
        <StatCard icon={DollarSign} label="Total Revenue" value={fmtMoney(s.revenue)} accent="bg-emerald-50 text-emerald-600" onClick={goOrders} />
        <StatCard icon={Receipt} label="Total Tax" value={fmtMoney(s.tax)} accent="bg-amber-50 text-amber-600" onClick={goOrders} />
        <StatCard icon={TrendingUp} label="Avg Order Value" value={fmtMoney(s.avg_order_value)} accent="bg-violet-50 text-violet-600" onClick={goOrders} />
      </div>

      {/* Row 1 — status + currency */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ChartCard title="Orders by Status" subtitle="Pipeline distribution" span="md:col-span-2" onClick={goOrders}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.by_status} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: '#fafafa' }} contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(v, name, p) => name === 'value' ? [`${v} orders`, 'Count'] : [v, name]} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} style={{ cursor: 'pointer' }}>
                {(data.by_status || []).map((row, i) => (
                  <Cell key={i} fill={STATUS_COLORS[row.name] || PALETTE[i % PALETTE.length]} style={{ cursor: 'pointer' }}
                    onClick={() => openOrdersDrill('status', row.name, 'Orders by Status')} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Orders by Currency" subtitle="Revenue exposure" span="md:col-span-2" onClick={goOrders}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data.by_currency} dataKey="value" nameKey="name" cx="40%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2} style={{ cursor: 'pointer' }}>
                {(data.by_currency || []).map((row, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} style={{ cursor: 'pointer' }}
                    onClick={() => openOrdersDrill('currency', row.name, 'Orders in')} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(v, name, p) => [`${v} orders · ${fmtMoney(p.payload.amount)}`, p.payload.name]} />
              <Legend verticalAlign="middle" align="right" layout="vertical" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 2 — revenue trend */}
      <ChartCard title="Monthly Revenue Trend" subtitle="Revenue and order volume — last 12 months" span="" onClick={goOrders}>
        {(data.monthly_revenue || []).length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No revenue data yet</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.monthly_revenue} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} tickFormatter={fmtMoneyShort} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                formatter={(v, name) => name === 'revenue' ? [fmtMoney(v), 'Revenue'] : [v, 'Orders']} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Area yAxisId="left" type="monotone" dataKey="revenue" fill="#10b981" stroke="#10b981" fillOpacity={0.18} strokeWidth={2.5} />
              <Bar yAxisId="right" dataKey="orders" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={20} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Row 3 — top customers tidy table */}
      <ChartCard title="Top 10 Customers by Revenue" subtitle="Sum of sales order gross totals" span="" onClick={() => navigate('/data/customers')}>
        {(data.top_customers || []).length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No customer revenue recorded</div>
        ) : (
          <div className="overflow-y-auto h-full">
            <table className="w-full text-sm">
              <tbody>
                {data.top_customers.map((row, i) => {
                  const maxValue = Math.max(...data.top_customers.map(r => r.value || 0));
                  const pct = maxValue > 0 ? Math.round((row.value / maxValue) * 100) : 0;
                  return (
                    <tr key={row.name || i}
                        onClick={() => openCustomerOrdersDrill(row.name)}
                        className="border-b border-zinc-100 last:border-0 hover:bg-indigo-50 cursor-pointer transition-colors"
                        data-testid={`so-customer-drill-${row.name}`}>
                      <td className="py-2 pr-3 w-6 text-xs text-zinc-400 tabular-nums text-right">{i + 1}.</td>
                      <td className="py-2 pr-3 text-left">
                        <div className="font-medium text-zinc-900 truncate">{row.name}</div>
                        <div className="mt-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right text-xs text-zinc-500 tabular-nums whitespace-nowrap">{row.orders} order{row.orders === 1 ? '' : 's'}</td>
                      <td className="py-2 pl-3 text-right font-mono tabular-nums text-zinc-900 whitespace-nowrap">
                        ${(row.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ChartCard>

      <DrillDownSheet
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        fetchUrl={drill?.fetchUrl}
        columns={drill?.columns || []}
        dataPath={drill?.dataPath}
        filter={drill?.filter}
      />

    </div>
  );
}
